import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

// Must be loaded from this exact URL; Cloudflare forbids proxying or caching it
const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// How long a submit waits for a token (e.g. while an interactive challenge is shown)
const TOKEN_WAIT_TIMEOUT_MS = 30000;

interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  theme?: "auto" | "light" | "dark";
  language?: string;
  appearance?: "always" | "execute" | "interaction-only";
  size?: "normal" | "flexible" | "compact";
  callback?: (token: string) => void;
  "error-callback"?: (code: string) => void;
  "expired-callback"?: () => void;
  "timeout-callback"?: () => void;
  "unsupported-callback"?: () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  isExpired: (widgetId: string) => boolean;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.onload = () =>
        window.turnstile
          ? resolve(window.turnstile)
          : reject(new Error("Turnstile failed to initialise"));
      script.onerror = () => {
        // Allow a later retry to inject the script again
        scriptPromise = null;
        script.remove();
        reject(new Error("Failed to load Turnstile"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

// Turnstile accepts ISO 639-1 codes; Chinese needs a region to pick a script
const toTurnstileLanguage = (language?: string) => {
  if (!language) return "auto";
  return language === "zh" ? "zh-CN" : language;
};

export class TurnstileTokenError extends Error {}

export interface TurnstileWidgetHandle {
  /** Resolves with a fresh token, waiting for the challenge if needed. */
  getToken: () => Promise<string>;
  /** Discard the current token and run a new challenge. Tokens are single-use. */
  reset: () => void;
}

interface TurnstileWidgetProps {
  siteKey: string | undefined;
  action: string;
  language?: string;
  loadErrorMessage: string;
  retryLabel: string;
}

type Waiter = {
  resolve: (token: string) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  ({ siteKey, action, language, loadErrorMessage, retryLabel }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const tokenRef = useRef<string | null>(null);
    const waitersRef = useRef<Waiter[]>([]);
    const [loadFailed, setLoadFailed] = useState(false);
    // Bumped to force a full re-render of the widget after a load failure
    const [attempt, setAttempt] = useState(0);

    const settleWaiters = useCallback((token: string | null, err?: Error) => {
      const waiters = waitersRef.current;
      waitersRef.current = [];
      waiters.forEach(({ resolve, reject, timer }) => {
        clearTimeout(timer);
        if (token) resolve(token);
        else reject(err ?? new TurnstileTokenError("Turnstile verification failed"));
      });
    }, []);

    useEffect(() => {
      if (!siteKey) {
        console.error("REACT_APP_TURNSTILE_SITE_KEY is not set; survey submissions will be rejected.");
        return;
      }

      let cancelled = false;
      setLoadFailed(false);

      loadTurnstile()
        .then((turnstile) => {
          if (cancelled || !containerRef.current) return;
          widgetIdRef.current = turnstile.render(containerRef.current, {
            sitekey: siteKey,
            action,
            theme: "dark",
            language: toTurnstileLanguage(language),
            // Managed mode stays invisible unless Cloudflare needs the visitor to interact
            appearance: "interaction-only",
            size: "flexible",
            callback: (token) => {
              tokenRef.current = token;
              settleWaiters(token);
            },
            "expired-callback": () => {
              // refresh-expired defaults to "auto", so a new token will follow
              tokenRef.current = null;
            },
            "timeout-callback": () => {
              tokenRef.current = null;
            },
            "error-callback": (code) => {
              console.warn("Turnstile error:", code);
              tokenRef.current = null;
              settleWaiters(null);
            },
            "unsupported-callback": () => {
              tokenRef.current = null;
              setLoadFailed(true);
              settleWaiters(null);
            },
          });
        })
        .catch((err) => {
          if (cancelled) return;
          console.error(err);
          setLoadFailed(true);
          settleWaiters(null);
        });

      return () => {
        cancelled = true;
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
        widgetIdRef.current = null;
        tokenRef.current = null;
        settleWaiters(null);
      };
    }, [siteKey, action, language, attempt, settleWaiters]);

    useImperativeHandle(
      ref,
      () => ({
        getToken: () => {
          const widgetId = widgetIdRef.current;
          const token = tokenRef.current;
          if (token && widgetId && !window.turnstile?.isExpired(widgetId)) {
            return Promise.resolve(token);
          }
          if (!siteKey || loadFailed) {
            return Promise.reject(new TurnstileTokenError("Turnstile is unavailable"));
          }
          return new Promise<string>((resolve, reject) => {
            const timer = setTimeout(() => {
              waitersRef.current = waitersRef.current.filter((w) => w.timer !== timer);
              reject(new TurnstileTokenError("Timed out waiting for Turnstile"));
            }, TOKEN_WAIT_TIMEOUT_MS);
            waitersRef.current.push({ resolve, reject, timer });
          });
        },
        reset: () => {
          tokenRef.current = null;
          if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
          }
        },
      }),
      [siteKey, loadFailed]
    );

    return (
      <div className="flex flex-col items-start gap-2">
        <div ref={containerRef} className="w-full flex justify-start" />
        {loadFailed && (
          <div className="text-left text-sm text-gray-400">
            <p>{loadErrorMessage}</p>
            <button
              type="button"
              className="mt-1 underline text-gray-200"
              onClick={() => setAttempt((n) => n + 1)}
            >
              {retryLabel}
            </button>
          </div>
        )}
      </div>
    );
  }
);

TurnstileWidget.displayName = "TurnstileWidget";
